import React, { useState } from 'react';
import Icon from '../../../components/openplay/OpenPlayIcon';

const ApplicationModal = ({ isOpen, onClose, eventTitle, paymentInfo, onSubmit }) => {
  const [skillLevel, setSkillLevel] = useState('');
  const [notes, setNotes] = useState('');

  const safePayment = paymentInfo || {};
  const hasPaymentInfo = Boolean(
    safePayment?.price != null ||
      String(safePayment?.bankName || '').trim() ||
      String(safePayment?.accountNumber || '').trim() ||
      String(safePayment?.accountHolder || '').trim() ||
      String(safePayment?.paymentDeadline || '').trim()
  );

  const formatDeadline = (value) => {
    const v = String(value || '').trim();
    if (!v) return '';
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    } catch {
      return v;
    }
  };

  const skillLevels = [
    { value: 'beginner', label: '초급', description: '처음 시작하거나 경험이 적음' },
    { value: 'intermediate', label: '중급', description: '기본기를 갖추고 경기 가능' },
    { value: 'advanced', label: '고급', description: '숙련된 실력으로 경쟁 가능' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!skillLevel) return;
    onSubmit({ skillLevel, notes });
    setSkillLevel('');
    setNotes('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal">
      <div className="absolute inset-0 bg-secondary" onClick={onClose} />

      <div className="absolute bottom-0 left-0 right-0 md:relative md:flex md:items-center md:justify-center md:min-h-screen md:p-6">
        <div
          className="bg-card rounded-t-xl md:rounded-xl shadow-elevation-5 max-h-[85vh] md:max-h-[80vh] overflow-y-auto md:w-full md:max-w-lg"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
            <h3 className="text-lg font-semibold font-heading text-foreground">참가 신청</h3>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:bg-muted transition-smooth touch-target"
              aria-label="닫기"
              type="button"
            >
              <Icon name="XMarkIcon" size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <h4 className="text-base font-semibold text-foreground mb-2">{eventTitle}</h4>
              <p className="text-sm text-muted-foreground caption">참가 신청을 위해 정보를 입력해주세요</p>
            </div>

            {hasPaymentInfo && (
              <div className="rounded-xl border border-border bg-muted/30 p-4">
                <div className="flex items-center gap-2">
                  <Icon name="CurrencyDollarIcon" size={18} className="text-foreground" />
                  <p className="text-sm font-semibold text-foreground">결제 안내</p>
                </div>
                <div className="mt-3 space-y-2 text-sm text-muted-foreground caption">
                  {safePayment?.price != null && (
                    <p>
                      참가비: <span className="text-foreground font-semibold">₩{Number(safePayment.price).toLocaleString()}</span>
                    </p>
                  )}
                  {(String(safePayment?.bankName || '').trim() || String(safePayment?.accountNumber || '').trim()) && (
                    <p>
                      계좌: <span className="text-foreground font-semibold">{String(safePayment.bankName || '').trim()} {String(safePayment.accountNumber || '').trim()}</span>
                    </p>
                  )}
                  {String(safePayment?.accountHolder || '').trim() && (
                    <p>
                      예금주: <span className="text-foreground font-semibold">{String(safePayment.accountHolder || '').trim()}</span>
                    </p>
                  )}
                  {String(safePayment?.paymentDeadline || '').trim() && (
                    <p>
                      마감: <span className="text-foreground font-semibold">{formatDeadline(safePayment.paymentDeadline)}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-foreground mb-3 caption">
                실력 수준 <span className="text-error">*</span>
              </label>
              <div className="space-y-2">
                {skillLevels.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => setSkillLevel(level.value)}
                    className={`w-full flex items-start gap-3 p-4 rounded-lg border transition-smooth touch-target ${
                      skillLevel === level.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:bg-muted'
                    }`}
                  >
                    <div className="flex-1 text-left">
                      <p className="font-medium">{level.label}</p>
                      <p className={`text-sm caption ${skillLevel === level.value ? 'opacity-90' : 'text-muted-foreground'}`}>
                        {level.description}
                      </p>
                    </div>
                    {skillLevel === level.value && <Icon name="CheckCircleIcon" variant="solid" size={24} />}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-semibold text-foreground mb-3 caption">
                추가 메시지 (선택)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="주최자에게 전달할 메시지를 입력하세요"
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth resize-none"
              />
              <p className="text-xs text-muted-foreground caption mt-2">{notes.length}/200자</p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 rounded-lg border border-border text-foreground hover:bg-muted transition-smooth touch-target font-medium"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={!skillLevel}
                className={`flex-1 px-6 py-3 rounded-lg font-medium transition-smooth touch-target ${
                  skillLevel
                    ? 'bg-primary text-primary-foreground hover:shadow-elevation-2 active:scale-97'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                신청하기
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;
